import React, { ChangeEvent, ReactElement, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Grid2, TextField, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { importOrder, updateBareGoodsOrderedQuantity } from '../../../redux/actions/orderActions';
import { BareGoodsEntity } from '../../../redux/reducers/goodsToOrderFormReducer';
import { RootState } from '../../../redux/reducers';
import { GoodsElementFormContainerBox } from '../../../common/ResponsiveStyle';
import { DimensionTypeDimensionSR, DimensionTypeSR } from '../../../common/Types';
import { getArticleOptionString } from '../../../common/components/autocomplete/AutocompleteHelper';
import { GoodsManipulationItemDto, GoodsStatus } from '../../../api/ClientApi';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { WarehouseClient } from '../../../api/Extensions';
import { addOrUpdateMultipleReduxGoods } from '../../../redux/actions/goodsActions';
import { convertGoodsIdsToBareGoodsEntities } from './OrderFormHelper';
import { filterUniqueBareGoodsByArticle } from '../../../redux/actions/helper';

type OrderFormGoodsWriteOffInTransportElementProps = {
    orderedBareGoods: BareGoodsEntity;
};

export default function OrderFormGoodsWriteOffInTransportElement(
    props: OrderFormGoodsWriteOffInTransportElementProps,
): ReactElement {
    const { orderedBareGoods } = props;

    const [reason, setReason] = useState<string>('');
    const [serverResponseStatus, setServerResponseStatus] = useState<SaveEntityStatus>(SaveEntityStatus.None);

    const order = useAppSelector((state: RootState) => state.orderForm.order);

    const oldOrderedGood = useMemo(() => {
        return filterUniqueBareGoodsByArticle(
            convertGoodsIdsToBareGoodsEntities(
                order?.goodsIds ?? [],
                order?.goods?.filter((g) => g.goodsStatus === GoodsStatus.InTransport) ?? [],
                [],
            ).filter((bge) => bge.article?.id === orderedBareGoods.article?.id),
        ).find((og) => og.article?.id === orderedBareGoods.article?.id);
    }, [order]);

    const dispatch = useAppDispatch();

    const goodsQuantityCompared = useMemo(() => {
        if (!!oldOrderedGood && !!orderedBareGoods) {
            return oldOrderedGood.orderedQuantity - orderedBareGoods.orderedQuantity;
        }

        return 0;
    }, [orderedBareGoods, oldOrderedGood]);

    const handleBareGoodsOrderedQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(updateBareGoodsOrderedQuantity({ index: orderedBareGoods.index, orderedQuantity: +value }));
        }
    };

    const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setReason(value);
    };

    const isWritingOffAllowed = !!reason && reason.trim() !== '' && goodsQuantityCompared > 0;

    const handleWriteOffElements = () => {
        setServerResponseStatus(SaveEntityStatus.Pending);
        const newGoodsManipulationItem = new GoodsManipulationItemDto({
            reason: reason,
            articleId: orderedBareGoods.article?.id,
            quantity: goodsQuantityCompared,
            goodsOriginOrderId: order?.id,
            executionTime: new Date(),
            isInTransport: true,
        });

        WarehouseClient.writeOffGoodsForOrderInTransport(newGoodsManipulationItem).then(
            (res) => {
                dispatch(addOrUpdateMultipleReduxGoods(res));
                dispatch(importOrder(order?.id ?? 0));
                setServerResponseStatus(SaveEntityStatus.Saved);
                setReason('');
            },
            () => {
                setServerResponseStatus(SaveEntityStatus.Error);
                setReason('');
            },
        );
    };

    return (
        !!orderedBareGoods && (
            <div>
                <GoodsElementFormContainerBox>
                    <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                        <Grid2 size={{ xs: 12, sm: 10 }} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{getArticleOptionString(orderedBareGoods.article)}</Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 2 }}>
                            <TextField
                                error={
                                    !!orderedBareGoods.orderedQuantity && orderedBareGoods.orderedQuantity > 0
                                        ? false
                                        : true
                                }
                                required
                                fullWidth
                                name="orderedQuantity"
                                label={
                                    orderedBareGoods.article?.dimensionType
                                        ? DimensionTypeSR[orderedBareGoods.article.dimensionType]
                                        : 'Količina'
                                }
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'decimal',
                                        style: { textAlign: 'center' },
                                    },
                                }}
                                type="number"
                                value={!!orderedBareGoods.orderedQuantity ? orderedBareGoods.orderedQuantity : ''}
                                variant="outlined"
                                onChange={handleBareGoodsOrderedQuantityChange}
                            />
                        </Grid2>
                    </Grid2>
                    <Box style={{ flexGrow: 1, minWidth: '15px' }} />
                </GoodsElementFormContainerBox>
                {goodsQuantityCompared > 0 && (
                    <Box sx={{ width: '100%', my: 2 }}>
                        <TextField
                            required
                            error={!reason || reason.trim() === ''}
                            fullWidth
                            name="description"
                            label={
                                'Reci nam šta se desilo sa ' +
                                goodsQuantityCompared +
                                ' "' +
                                orderedBareGoods.article?.title +
                                '"'
                            }
                            multiline
                            rows={6}
                            value={reason ?? ''}
                            variant="outlined"
                            onChange={handleReasonChange}
                        />
                    </Box>
                )}
                {goodsQuantityCompared < 0 && (
                    <Typography color="error">
                        {'Poslato je ' +
                            oldOrderedGood?.orderedQuantity +
                            (orderedBareGoods.article?.dimensionType
                                ? ' ' + DimensionTypeDimensionSR[orderedBareGoods.article?.dimensionType]
                                : '') +
                            ' "' +
                            orderedBareGoods.article?.title +
                            '".' +
                            ' Da li je stiglo ' +
                            Math.abs(goodsQuantityCompared) +
                            (orderedBareGoods.article?.dimensionType
                                ? ' ' + DimensionTypeDimensionSR[orderedBareGoods.article?.dimensionType]
                                : '') +
                            ' više nego što je naručeno?'}
                    </Typography>
                )}
                {goodsQuantityCompared > 0 && (
                    <Box sx={{ width: '100%' }}>
                        <Button
                            sx={{ width: '100%' }}
                            variant="outlined"
                            onClick={handleWriteOffElements}
                            disabled={!isWritingOffAllowed || serverResponseStatus === SaveEntityStatus.Pending}
                        >
                            {serverResponseStatus === SaveEntityStatus.Pending ? (
                                <div>
                                    <CircularProgress size={15} color="inherit" />
                                </div>
                            ) : isWritingOffAllowed ? (
                                'Sačuvaj promenu'
                            ) : (
                                'Unesite razlog za otpisivanje'
                            )}
                        </Button>
                    </Box>
                )}
            </div>
        )
    );
}
